import * as THREE from "three";

export enum Role {
  VILLAGER = "VILLAGER",
  MAFIA = "MAFIA",
  SHERIFF = "SHERIFF",
  DOCTOR = "DOCTOR",
}

export enum GamePhase {
  SETUP = "SETUP",
  GREETING = "GREETING",
  NIGHT_0 = "NIGHT_0",
  NIGHT = "NIGHT",
  DAY = "DAY",
  VOTING = "VOTING",
  GAME_OVER = "GAME_OVER",
}

export interface Character {
  model: THREE.Group;
  position: THREE.Vector3;
  update: () => void;
  setColor: (hex: string) => void;
  setAlive: (isAlive: boolean) => void;
  setHuman: (isHuman: boolean) => void;
}

export type ChatMessageType = "player" | "llm" | "system";

export interface Message {
  text: string;
  sender: ChatMessageType;
  timestamp: string;
  playerName?: string;
  color?: string;
}

export interface ChatHistoryEntry {
  speaker?: string | null;
  player_name?: string | null;
  role?: string | null;
  phase?: string | null;
  content?: string | null;
  type?: string | null;
  is_human?: boolean;
}

export interface PlayerSnapshot {
  model_name?: string;
  player_name?: string;
  role?: string;
  alive?: boolean;
  is_human?: boolean;
}

export interface PendingActionMetadata {
  [key: string]: unknown;
  action?: string;
  phase?: string;
  phase_type?: string;
  round_number?: number;
  round?: number;
  available_players?: PlayerSnapshot[];
}

export interface PendingAction {
  actionId: string;
  prompt: string;
  metadata: PendingActionMetadata;
  player: PlayerSnapshot;
}

export interface Player {
  id: string;
  name: string;
  role: Role | null;
  actualRole?: Role | null;
  isAlive: boolean;
  character?: Character;
  isHuman: boolean;
  color?: string;
  colorIndex?: number;
}

export interface GameState {
  phase: GamePhase;
  day: number;
  players: Player[];
  messages: Message[];
  playerRole: Role | null;
  humanPlayerName: string | null;
  humanPlayerColor: string | null;
  winner: string | null;
  pendingAction: PendingAction | null;
  gameId: string | null;
  connected: boolean;
  lastActionId: string | null;
  isPaused: boolean;
  debugEnabled: boolean;
}

export interface ServerEvent<T = unknown> {
  type: string;
  payload: T;
  timestamp?: number;
  game_id?: string;
}

export interface GameEventPayload {
  [key: string]: unknown;
  event_type?: string;
  details?: string;
  message?: string;
  briefing?: string;
  summary?: string;
  speaker?: string;
  content?: string;
  player_name?: string;
  model_name?: string;
  role?: string;
  response?: string;
  action?: string;
  winner?: string;
  prompt?: string;
  cause?: string;
  phase?: string;
  round_number?: number;
  teammates?: string[];
}

export interface DebugSnapshot {
  game_id: string;
  phase: string;
  round: number;
  paused: boolean;
  alive_counts: {
    total: number;
    mafia: number;
    villagers: number;
    doctor: number;
  };
  players: Array<{
    name: string;
    role: string;
    alive: boolean;
    is_human: boolean;
  }>;
  game_state: string;
  recent_events?: Array<Record<string, unknown>>;
  pending_action?: Record<string, unknown> | null;
}
